/** @jsxImportSource @emotion/react */
import PropTypes from "prop-types";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { faGrid2, faThumbTack } from "@fortawesome/pro-regular-svg-icons";
import {
  useSetDescriptionOnMount,
  useSetTitleOnMount,
} from "components/hooks/useSetTitle";
import { useTrackWithMixpanelOnce } from "trackers/mixpanel";
import Colors from "styles/colors";

import { Tabs, TabsClassNames } from "components/molecules/Tabs.molecule";
import { ResponsiveSection } from "styles/container-elements";

import { Icon } from "components/atoms/Icon.atom";
import { Text } from "components/atoms/Text.atom";
import { Dashboard } from "components/templates/Dashboard.template";
import { BrowserStorage } from "utils/browser-storage.utils";
import InventoryViewDetailsSearchBarContainer from "../details/search/InventoryView.Details.SearchBarContainer";
import SearchFiltersContainer from "../details/search/InventoryView.Details.FilterSectionContainer";
import { InitialETARange } from "../components/InitialETARange.PieChart";
import {
  selectedTab,
  tabsListStyle,
  defaultTab,
} from "../components/InventoryNavigationTabs";
import { MediaQueries } from "components/responsive";
import {
  PanelGroup,
  PanelGroupContent,
} from "components/molecules/PanelGroup.molecule";
import { GateReleaseThreshold } from "../components/GateReleaseThreshold.PieChart";
import { TopTenDaysOnSite } from "../components/TopTenDaysOnSite.Table";
import { TopExceededCapacity } from "../components/TopExceededCapacity.Table";
import { SearchByLocation } from "./components/SearchByLocation";

const InventoryViewDashboard = ({
  solutionId,
  redirectToDetailsView,
  redirectToPinnedPage,
  redirectToInsightsView,
  setSearchFilter,
  resetSearchAndFilters,
  fetchInitialETALocations,
  initialETALocations,
  isInitialETALocationsLoading,
  isInitialETALocationsError,
  fetchGateReleaseThresholdLocations,
  gateReleaseThresholdLocations,
  isGateReleaseThresholdLocationsLoading,
  isGateReleaseThresholdLocationsError,
  isInventoryViewFeatureEnabled,
  currentOrganization,
}) => {
  const { t } = useTranslation("inv-dashboard");

  useSetTitleOnMount(t("inv-dashboard:InventoryView"));

  useSetDescriptionOnMount(
    t(
      "inv-dashboard:All InventoryView Locations, their current capacity and forecasted capacity",
    ),
  );

  useTrackWithMixpanelOnce("Viewed Page: InventoryView / Dashboard");

  const [selectedTabIndex, setSelectedTabIndex] = useState(0);

  const fvId = currentOrganization?.fv_id ?? "";

  const onTabChange = useCallback((index) => {
    setSelectedTabIndex(index);
  }, []);

  // reset browser's locationTimezone
  BrowserStorage.locationTimezone = null;

  return (
    <ResponsiveSection
      style={{
        justifyContent: "flex-start",
        padding: "1em 2em 2em",
        background: Colors.background.LIGHT_GRAY,
        overflowY: "auto",
        height: "100%",
      }}
    >
      <div style={{ display: "none" }}>
        <Dashboard
          SearchBarContainer={InventoryViewDetailsSearchBarContainer}
          showFilters={false}
          toggleShowFilters={null}
          FiltersContainer={SearchFiltersContainer}
        ></Dashboard>
      </div>

      <Tabs
        borderColor={Colors.inventoryView.BLUE}
        css={{
          [TabsClassNames.TabList]: {
            ...tabsListStyle,
          },
          [TabsClassNames.Tab]: {
            ...defaultTab,
          },
          [TabsClassNames.TabSelected]: {
            ...selectedTab,
          },
        }}
        selectedIndex={selectedTabIndex}
        onSelect={onTabChange}
      >
        <Tabs.TabList>
          {isInventoryViewFeatureEnabled && (
            <Tabs.Tab>
              <Icon src={faGrid2} />
              <Text
                css={{
                  fontSize: "inherit",
                  textUnderlineOffset: "2px",
                  textDecoration: "underline",
                  marginLeft: "5px",
                }}
              >
                {t("inv-dashboard:Overview")}
              </Text>
            </Tabs.Tab>
          )}
          <Tabs.Tab onClick={(evt) => redirectToPinnedPage()}>
            <Icon css={{ transform: "rotate(45deg)" }} src={faThumbTack} />
            <Text
              css={{
                fontSize: "inherit",
                textUnderlineOffset: "2px",
                textDecoration: "underline",
                marginLeft: "10px",
              }}
            >
              {t("inv-dashboard:Pinned")}
            </Text>
          </Tabs.Tab>
          <SearchByLocation
            fvId={fvId}
            redirectToInsightsView={redirectToInsightsView}
          />
        </Tabs.TabList>
        {isInventoryViewFeatureEnabled && (
          <Tabs.TabPanel
            style={{ border: 0, background: "none", boxShadow: "none" }}
          >
            <div
              css={{
                display: "grid",
                gap: "1em",
                [MediaQueries.mediumAndUp]: {
                  gridTemplateColumns: "repeat(3, 1fr)",
                },
                [MediaQueries.medium]: {
                  gridTemplateColumns: "repeat(3, 1fr)",
                },
                [MediaQueries.smallAndDown]: {
                  gridTemplateColumns: "1fr",
                },
              }}
            >
              <div
                css={{
                  display: "flex",
                  flexDirection: "column",
                  rowGap: "1em",
                }}
              >
                <InitialETARange
                  solutionId={solutionId}
                  setSearchFilter={setSearchFilter}
                  redirectToDetailsView={redirectToDetailsView}
                  fetchLocations={fetchInitialETALocations}
                  locationsData={initialETALocations}
                  isLocationsLoading={isInitialETALocationsLoading}
                  isError={isInitialETALocationsError}
                  resetSearchAndFilters={resetSearchAndFilters}
                />
                <GateReleaseThreshold
                  solutionId={solutionId}
                  setSearchFilter={setSearchFilter}
                  redirectToDetailsView={redirectToDetailsView}
                  fetchLocations={fetchGateReleaseThresholdLocations}
                  locationsData={gateReleaseThresholdLocations}
                  isLocationsLoading={isGateReleaseThresholdLocationsLoading}
                  isError={isGateReleaseThresholdLocationsError}
                  resetSearchAndFilters={resetSearchAndFilters}
                />
              </div>
              <PanelGroup>
                <PanelGroupContent>
                  <TopTenDaysOnSite
                    solutionId={solutionId}
                    setSearchFilter={setSearchFilter}
                    redirectToDetailsView={redirectToDetailsView}
                    resetSearchAndFilters={resetSearchAndFilters}
                  />
                </PanelGroupContent>
              </PanelGroup>
              <PanelGroup>
                <PanelGroupContent>
                  <TopExceededCapacity
                    redirectToInsightsView={redirectToInsightsView}
                    resetSearchAndFilters={resetSearchAndFilters}
                  />
                </PanelGroupContent>
              </PanelGroup>
            </div>
          </Tabs.TabPanel>
        )}

        <Tabs.TabPanel
          style={{ border: 0, background: "none", boxShadow: "none" }}
        >
          <Text>Empty Tab</Text>
        </Tabs.TabPanel>
      </Tabs>
    </ResponsiveSection>
  );
};

InventoryViewDashboard.propTypes = {
  solutionId: PropTypes.string,
  redirectToDetailsView: PropTypes.func,
  redirectToPinnedPage: PropTypes.func,
  redirectToInsightsView: PropTypes.func,
  setSearchFilter: PropTypes.func,
  resetSearchAndFilters: PropTypes.func,
  currentOrganization: PropTypes.object,
  fetchInitialETALocations: PropTypes.func,
  initialETALocations: PropTypes.array,
  isInitialETALocationsLoading: PropTypes.bool,
  isInitialETALocationsError: PropTypes.bool,
  fetchGateReleaseThresholdLocations: PropTypes.func,
  gateReleaseThresholdLocations: PropTypes.array,
  isGateReleaseThresholdLocationsLoading: PropTypes.bool,
  isGateReleaseThresholdLocationsError: PropTypes.bool,
  isInventoryViewFeatureEnabled: PropTypes.bool,
};

export default InventoryViewDashboard;
